/**
 * An image behind `authenticate` (SEC-5).
 *
 * GET /uploads/:id requires a Bearer header, and a plain `<img src>` can never
 * send one — the browser makes that request itself, outside the API client. So
 * a receipt rendered that way is a 401 and a broken image, which is exactly how
 * receipts went unseen at approval time. This fetches the bytes through the
 * API client (token attached, single-flight refresh included) and shows them
 * from a blob URL instead.
 *
 * The blob is cached by React Query with no expiry: a stored upload never
 * changes (its name is a fresh ULID), so there is nothing to refetch.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ImgHTMLAttributes } from 'react';

import { api } from '../api/client.js';

export const uploadKey = (id: string) => ['upload', id] as const;

async function fetchUpload(id: string): Promise<Blob> {
  const response = await api.get<Blob>(`/uploads/${encodeURIComponent(id)}`, { responseType: 'blob' });
  return response.data;
}

export function useUploadBlob(id: string | null): { blob: Blob | undefined; isError: boolean; isLoading: boolean } {
  const { data, isError, isLoading } = useQuery({
    queryKey: uploadKey(id ?? ''),
    queryFn: () => fetchUpload(id!),
    enabled: id !== null,
    staleTime: Infinity,
  });

  return { blob: data, isError, isLoading: id !== null && isLoading };
}

/** An object URL for a blob, revoked when the blob changes or the caller unmounts. */
export function useObjectUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }

    const created = URL.createObjectURL(blob);
    setUrl(created);
    // Each object URL pins its blob in memory until revoked.
    return () => URL.revokeObjectURL(created);
  }, [blob]);

  return url;
}

type ProtectedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  /** The upload id, e.g. `01J…Z.jpg` — never a URL. */
  uploadId: string;
  /** Shown while loading or if the image cannot be fetched. */
  fallback?: JSX.Element;
};

export function ProtectedImage({ uploadId, fallback, alt, ...rest }: ProtectedImageProps): JSX.Element | null {
  const { blob } = useUploadBlob(uploadId);
  const url = useObjectUrl(blob);

  if (url === null) return fallback ?? null;

  return <img src={url} alt={alt} {...rest} />;
}
